/** Counters shown by the call diagnostics panel: they reveal duplicated connections and unanswered offers. */
export const callCounters = {
  openSockets: 0,
  peers: 0,
  offersSent: 0,
  offerResends: 0,
  offersUnanswered: 0,
  answersSent: 0,
  reconnections: 0,
};

export interface CallDiagnostics {
  /** 'direct' (host/srflx) or 'relay' (through TURN), null until a candidate pair is selected */
  path: 'direct' | 'relay' | null;
  protocol: string | null;
  rttMs: number | null;
  videoCodec: string | null;
  decoder: string | null;
  hardwareDecoder: boolean | null;
  width: number | null;
  height: number | null;
  fps: number | null;
  videoKbps: number | null;
  videoLossPct: number | null;
  freezes: number | null;
  videoBufferMs: number | null;
  audioBufferMs: number | null;
  audioLossPct: number | null;
  micKbps: number | null;
  /** Network one way (RTT / 2) plus the receive buffer: the delay the panel can see, the headset adds its own */
  estimatedDelayMs: number | null;
}

/** Reads the panel side of a call from two consecutive getStats() reports (rates are computed between the two). */
export function readCallDiagnostics(report: RTCStatsReport, last?: RTCStatsReport): CallDiagnostics {
  const stats = new Map<string, any>();
  report.forEach(stat => stats.set(stat.id, stat));
  const previous = new Map<string, any>();
  last?.forEach(stat => previous.set(stat.id, stat));

  const result: CallDiagnostics = {
    path: null, protocol: null, rttMs: null, videoCodec: null, decoder: null, hardwareDecoder: null,
    width: null, height: null, fps: null, videoKbps: null, videoLossPct: null, freezes: null,
    videoBufferMs: null, audioBufferMs: null, audioLossPct: null, micKbps: null, estimatedDelayMs: null,
  };

  let pair: any = null;
  stats.forEach(stat => {
    if (stat.type === 'transport' && stat.selectedCandidatePairId) {
      pair = stats.get(stat.selectedCandidatePairId) ?? pair;
    }
  });
  if (!pair) {
    stats.forEach(stat => {
      if (stat.type === 'candidate-pair' && stat.nominated && stat.state === 'succeeded') {
        pair = stat;
      }
    });
  }
  if (pair) {
    const local = stats.get(pair.localCandidateId);
    const remote = stats.get(pair.remoteCandidateId);
    result.path = local?.candidateType === 'relay' || remote?.candidateType === 'relay' ? 'relay' : 'direct';
    result.protocol = local?.relayProtocol ?? local?.protocol ?? null;
    if (typeof pair.currentRoundTripTime === 'number') {
      result.rttMs = Math.round(pair.currentRoundTripTime * 1000);
    }
  }

  const rate = (stat: any, field: string) => {
    const before = previous.get(stat.id);
    if (!before || typeof stat[field] !== 'number' || stat.timestamp <= before.timestamp) {
      return null;
    }
    return Math.round(8 * (stat[field] - before[field]) / (stat.timestamp - before.timestamp));
  };
  const lossPct = (stat: any) => {
    const before = previous.get(stat.id);
    if (!before) {
      return null;
    }
    const lost = (stat.packetsLost ?? 0) - (before.packetsLost ?? 0);
    const received = (stat.packetsReceived ?? 0) - (before.packetsReceived ?? 0);
    return lost + received > 0 ? Math.round(1000 * Math.max(lost, 0) / (lost + received)) / 10 : 0;
  };
  // Average time the media emitted in the last interval waited in the receive buffer
  const bufferMs = (stat: any) => {
    const before = previous.get(stat.id);
    const emitted = (stat.jitterBufferEmittedCount ?? 0) - (before?.jitterBufferEmittedCount ?? 0);
    const delay = (stat.jitterBufferDelay ?? 0) - (before?.jitterBufferDelay ?? 0);
    return emitted > 0 ? Math.round(1000 * delay / emitted) : null;
  };

  stats.forEach(stat => {
    if (stat.type === 'inbound-rtp' && stat.kind === 'video') {
      result.videoCodec = stats.get(stat.codecId)?.mimeType?.replace('video/', '') ?? null;
      result.decoder = stat.decoderImplementation ?? null;
      result.hardwareDecoder = typeof stat.powerEfficientDecoder === 'boolean' ? stat.powerEfficientDecoder : null;
      result.width = stat.frameWidth ?? null;
      result.height = stat.frameHeight ?? null;
      result.fps = typeof stat.framesPerSecond === 'number' ? Math.round(stat.framesPerSecond) : null;
      result.videoKbps = rate(stat, 'bytesReceived');
      result.videoLossPct = lossPct(stat);
      result.freezes = stat.freezeCount ?? null;
      result.videoBufferMs = bufferMs(stat);
    } else if (stat.type === 'inbound-rtp' && stat.kind === 'audio') {
      result.audioBufferMs = bufferMs(stat);
      result.audioLossPct = lossPct(stat);
    } else if (stat.type === 'outbound-rtp' && stat.kind === 'audio') {
      result.micKbps = rate(stat, 'bytesSent');
    }
  });

  const buffer = Math.max(result.videoBufferMs ?? 0, result.audioBufferMs ?? 0);
  if (result.rttMs !== null || buffer > 0) {
    result.estimatedDelayMs = Math.round((result.rttMs ?? 0) / 2 + buffer);
  }
  return result;
}
